import { useEffect, useState } from "react";

/**
 * Hook that manages browser notification permission state.
 * Automatically requests permission when push consent is given and permission is in default state.
 *
 * @param hasPushConsent - Whether user has consented to push notifications
 * @returns Current browser notification permission status ('default', 'granted', or 'denied')
 */
export function useBrowserNotificationPermission(
  hasPushConsent: boolean,
): NotificationPermission {
  const supportsNotifications =
    typeof window !== "undefined" && "Notification" in window;

  const [permission, setPermission] = useState<NotificationPermission>(
    supportsNotifications ? Notification.permission : "default",
  );

  useEffect(() => {
    if (!supportsNotifications) {
      setPermission("default");

      return;
    }

    const currentPermission = Notification.permission;

    setPermission(currentPermission);

    if (hasPushConsent && currentPermission === "default") {
      Notification.requestPermission()
        .then((newPermission) => {
          setPermission(newPermission);
        })
        .catch((error) => {
          console.error(
            "[Airship] Failed to request notification permission:",
            error,
          );
        });
    }
  }, [hasPushConsent, supportsNotifications]);

  return permission;
}
